/**
 * Shared "Kind" (internal / customer) filter machinery for catalog
 * setup pages — currently products and service roles. Both pages share
 * the same three-value URL param (`?kind=all|customer|internal`), the
 * same badge palette, and the same `internal: boolean | undefined`
 * mapping to the typed list API.
 *
 * Keep this file in lock-step with the API's `internal` filter on the
 * matching domain (products and service_roles both accept
 * `?internal=true|false`). If a third domain ever grows a kind picker,
 * import these helpers rather than re-implementing — the duplication
 * pattern is exactly what the docs/CLAUDE.md G10 ("reuse before
 * adding") guidance is meant to prevent.
 */

export const KIND_FILTERS = ['all', 'customer', 'internal'] as const;
export type KindFilter = (typeof KIND_FILTERS)[number];

export const KIND_LABELS: Record<KindFilter, string> = {
  all: 'All kinds',
  customer: 'Customer',
  internal: 'Internal',
};

export const DEFAULT_KIND: KindFilter = 'all';

/**
 * Translate a UI filter selection into the typed list API's `internal`
 * boolean. Returning `undefined` for `'all'` means "don't filter".
 */
export function kindFilterToQuery(filter: KindFilter): boolean | undefined {
  if (filter === 'internal') return true;
  if (filter === 'customer') return false;
  return undefined;
}

/**
 * Two-state chip mirroring the products and service-roles list
 * tables. Pre-existing convention: amber for internal, sky for
 * customer; the dark-mode variants keep the same contrast as the
 * other status badges in the app.
 */
export function KindBadge({ internal }: { internal: boolean }) {
  if (internal) {
    return (
      <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900 dark:bg-amber-900/40 dark:text-amber-200">
        Internal
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-sky-100 px-2 py-0.5 text-xs font-medium text-sky-900 dark:bg-sky-900/40 dark:text-sky-200">
      Customer
    </span>
  );
}
